/**
 * La table des modules du back-office — seize destinations, quatre groupes.
 *
 * **L'ordre et les groupes sont ceux de la maquette**, et pas d'un plan qui
 * s'en approche : `designs/client-app/project/Jëliko - Guichet, Back-office,
 * Marchand.dc.html`, constante `modDefs`. Chaque module y porte aussi son
 * icône, reprise ici par son nom. Un test confronte la table à ce fichier —
 * `test/app/sidebar-maquette.test.tsx` — pour que la phrase ci-dessus reste
 * vérifiable au lieu d'être une affirmation d'en-tête. La version précédente
 * annonçait déjà « dans l'ordre de la maquette » en rangeant les entreprises
 * dans le mauvais groupe : c'est exactement ce que ce test empêche.
 *
 * **Aucun libellé français ici.** Chaque module porte une clé de traduction ;
 * le libellé vit dans `src/i18n/locales/fr.json`. C'est ce qui permet
 * d'ajouter le wolof sans toucher à ce fichier, et
 * `hardcoded-strings-check.ts` le refuse autrement.
 *
 * **Aucun écran métier.** Les seize routes existent et sont atteignables ;
 * chacune rend l'écran provisoire. C'est ce qui permettra aux epics 1 à 14 d'y
 * poser leur contenu sans retoucher le routage — le routeur **dérive** de
 * cette table, il ne la double pas.
 */

import type { IconName } from './module-icons.js';

/** Un groupe de navigation, dans l'ordre de la maquette. */
export type ModuleGroup = 'steering' | 'operations' | 'networkAndFinance' | 'system';

/** Un module du back-office. */
export interface BackOfficeModule {
  /** Identifiant stable, employé comme clé de traduction et de test. */
  readonly id: string;
  /** Chemin de la route, en anglais comme le reste du code. */
  readonly path: string;
  /** Groupe de navigation auquel il appartient. */
  readonly group: ModuleGroup;
  /** L'icône que la maquette pose devant son libellé. */
  readonly icon: IconName;
  /**
   * Le module de la matrice d'habilitations qui gouverne cette entrée, s'il existe.
   *
   * C'est la colonne `module` d'`admin.bo_action`, telle que l'annexe F la porte —
   * `KYC`, `CLIENTS`, `TRANSACTIONS`, `AUDIT`. Elle n'est renseignée que là où
   * l'annexe **sait répondre**, c'est-à-dire là où elle porte au moins une action de
   * consultation. Les autres entrées n'en ont pas, et restent visibles : l'annexe est
   * « un extrait, configurable », et masquer ce dont elle ne parle pas encore
   * confondrait « interdit » avec « pas encore décrit ».
   *
   * **Elle n'autorise rien.** Le serveur refuse ce qu'il refusait déjà, par ses
   * propres contrôles (`EX-BO-002`) ; ceci ne fait que cesser de proposer une
   * destination qui échouera toujours.
   */
  readonly permissionModule?: string;
}

/** Les quatre groupes, dans leur ordre d'affichage. */
export const moduleGroups: readonly ModuleGroup[] = [
  'steering',
  'operations',
  'networkAndFinance',
  'system',
];

/**
 * Les seize modules du lot 1, **dans l'ordre de la maquette**.
 *
 * L'ordre de cette table est celui de la barre latérale : le routeur et la
 * navigation en dérivent tous les deux, et une seconde liste écrite à la main
 * finirait par diverger de celle-ci.
 *
 * Un test la plancher-teste : la vider ou en retirer un module doit faire
 * rougir la suite, sans quoi le routeur pourrait perdre la moitié du
 * back-office en silence.
 */
export const backOfficeModules: readonly BackOfficeModule[] = [
  { id: 'dashboard', path: '/dashboard', group: 'steering', icon: 'grid' },
  {
    id: 'reporting',
    path: '/reporting',
    group: 'steering',
    icon: 'chart',
    permissionModule: 'REPORTING',
  },
  {
    id: 'customers',
    path: '/customers',
    group: 'operations',
    icon: 'users',
    permissionModule: 'CLIENTS',
  },
  {
    id: 'transactions',
    path: '/transactions',
    group: 'operations',
    icon: 'swap',
    permissionModule: 'TRANSACTIONS',
  },
  {
    id: 'identityReview',
    path: '/identity-review',
    group: 'operations',
    icon: 'idCard',
    permissionModule: 'KYC',
  },
  { id: 'businessKyb', path: '/business-kyb', group: 'operations', icon: 'fileText' },
  {
    id: 'compliance',
    path: '/compliance',
    group: 'operations',
    icon: 'shield',
    permissionModule: 'CONFORMITE',
  },
  {
    id: 'support',
    path: '/support',
    group: 'operations',
    icon: 'lifeBuoy',
    permissionModule: 'SUPPORT',
  },
  {
    id: 'outletNetwork',
    path: '/outlet-network',
    group: 'networkAndFinance',
    icon: 'landmark',
    permissionModule: 'RESEAU',
  },
  { id: 'merchants', path: '/merchants', group: 'networkAndFinance', icon: 'store' },
  {
    id: 'finance',
    path: '/finance',
    group: 'networkAndFinance',
    icon: 'wallet',
    permissionModule: 'RECONCILIATION',
  },
  {
    id: 'pricing',
    path: '/pricing',
    group: 'networkAndFinance',
    icon: 'tag',
    permissionModule: 'TARIFICATION',
  },
  {
    id: 'communityProducts',
    path: '/community-products',
    group: 'networkAndFinance',
    icon: 'users',
    // V102 : `TONTINE_VIEW` est la première consultation de ce module, et la
    // matrice sait donc enfin répondre — le menu cesse de proposer la
    // supervision au produit et à la technique, à qui elle est refusée.
    permissionModule: 'TONTINE',
  },
  {
    id: 'systemSettings',
    path: '/system-settings',
    group: 'system',
    icon: 'sliders',
    permissionModule: 'PARAMETRES',
  },
  {
    id: 'usersRoles',
    path: '/users-roles',
    group: 'system',
    icon: 'users',
    permissionModule: 'UTILISATEURS',
  },
  {
    id: 'auditLog',
    path: '/audit-log',
    group: 'system',
    icon: 'fileText',
    permissionModule: 'AUDIT',
  },
];

/**
 * Les modules dont l'écran métier est **livré**.
 *
 * Les seize modules restent seize : cette table ne les double pas, elle dit
 * lesquels ont cessé de rendre l'écran provisoire. Un module absent d'ici rend
 * `PlaceholderScreen`, qui nomme le module et la route d'où viendra son
 * contenu — c'est ce qui distingue « pas encore écrit » d'une page blanche.
 *
 * `identityReview` est le premier : `B-09` et `B-10` arrivent avec l'epic du
 * dossier d'identité. `auditLog` est le second, et il vient d'ailleurs : sa
 * route `GET /api/backoffice/audit` était **servie, éprouvée, et appelée par
 * aucun écran** — la famille de défaut que `D-25`, `D-30` et `D-35` décrivent.
 *
 * `dashboard` est le troisième, et il vient d'un degré plus grave encore : la
 * double validation d'`EX-BO-003` savait mettre une action en attente et savait
 * la décider, et **aucune couche ne savait dire ce qui attend** — ni dépôt, ni
 * service, ni route, ni écran. Une demande ne pouvait être approuvée que par
 * quelqu'un qui en connaissait déjà l'identifiant, alors que la règle exige
 * précisément un *autre* signataire. `DashboardScreen` rend la carte « Double
 * validation en attente » de la maquette, et **elle seule** : les huit autres
 * blocs du dessin n'ont aucune route, et l'écran le dit plutôt que de les
 * inventer.
 *
 * `customers` est le quatrième, et il répète le même défaut d'un cran plus bas :
 * le module n'était servi que par `GET /customers/{id}`, si bien qu'une fiche ne
 * pouvait s'ouvrir que par quelqu'un qui en connaissait déjà l'`UUID`. La couche
 * qui permet de **trouver** manquait sous la couche qui permet d'agir. Une route
 * de recherche a été écrite, sous le même droit `CUSTOMER_VIEW`, et le contrat
 * sert enfin les trois colonnes que la maquette dessine et qu'il taisait —
 * niveau KYC, solde, dernière activité —, toutes trois déjà stockées par le
 * système.
 *
 * `finance` est le cinquième, et il répète le défaut d'origine à sa plus grande
 * échelle : `ReconciliationApi` servait **quatre** routes — le rapport d'une
 * journée, la file des écarts, un écart avec son historique, les périodes
 * comptables —, toutes éprouvées, et **aucune n'était appelée par un écran**.
 * C'est la neuvième occurrence de la famille de `D-25`, `D-30`, `D-35`, `D-45`
 * et `D-57` ; depuis que `D-57` est soldée,
 * `tools/ci/test_routes_appelees_et_servies.py` les **nomme** au lieu de les
 * compter, et ces quatre-là étaient quatre des huit. `FinanceScreen` les
 * consomme toutes les quatre, et demande la clôture d'une période par
 * `PERIOD_CLOSE` — en ✔✔, donc par la file de double validation déjà livrée,
 * jamais par un second mécanisme.
 *
 * `transactions` est le septième, et il prend la famille par l'AUTRE bout. Les
 * six précédents corrigeaient une route servie qu'aucun écran n'appelait ;
 * celui-ci corrige un **droit accordé que rien ne servait** : `TRANSACTION_VIEW`
 * existe dans `admin.bo_action` depuis la V7, accordé à six rôles sur sept et
 * refusé au seul `PRODUCT_ADMIN`, l'annexe F le décrit, `MatriceDHabilitationsTest`
 * l'éprouve ligne par ligne — et **aucune route ne servait de transaction au
 * back-office**. Six rôles portaient une habilitation qui ne pouvait s'exercer
 * nulle part. `GET /api/backoffice/transactions` et `TransactionsScreen` sont
 * livrés ensemble, comme les deux moitiés doivent l'être.
 *
 * `compliance` est le sixième, et il porte la **dixième** occurrence de la même
 * famille : `SurveillanceApi` sert sept routes — la file d'alertes,
 * l'investigation en un clic, les règles et leurs seuils, les rejeux sur
 * historique, le reporting, le dossier de soupçon et son export —, toutes
 * éprouvées, **aucune appelée**, pendant que le moteur levait de vraies alertes
 * en base et que le module rendait encore l'écran provisoire. `ComplianceScreen`
 * les consomme toutes les sept, et n'offre **aucune** des trois décisions du
 * dessin : elles s'écrivent par la route unique des actions, le serveur exige
 * une note conservée avec la décision, et la déclaration de soupçon passe en
 * double validation — un bouton qui déciderait sans recueillir la note
 * écrirait une décision vide et irréversible.
 *
 * **L'écran de chaque module livré vit dans `App.tsx`**, qui seul connaît React ;
 * `routing.test.tsx` confronte les deux tables pour qu'un module déclaré livré
 * sans écran ne rende pas une page blanche.
 */
export const deliveredModuleIds: ReadonlySet<string> = new Set([
  'identityReview',
  'auditLog',
  'dashboard',
  'customers',
  'finance',
  'compliance',
  'transactions',
  // Le catalogue d'états, la production scellée et la vérification du
  // chaînage : quatre routes servies, éprouvées, et appelées par aucun écran
  // jusqu'ici.
  'reporting',
  // La file des réclamations et le dossier d'un ticket : deux routes
  // servies, pendant que le socle exécutait DÉJÀ six actions d'écriture.
  'support',
  // La liquidité du réseau et les demandes de rééquilibrage. Une TROISIÈME
  // route a dû être écrite : l'affectation exigeait un identifiant d'agent que
  // rien ne servait.
  'outletNetwork',
  // SEPT routes servies et appelées par personne : le module qui en portait
  // le plus de toute la famille D-25 / D-30 / D-35 / D-45 / D-57.
  'pricing',
  // Les DEUX DERNIERS, et les seuls qui ont demandé d'écrire du serveur :
  // ils ne portaient QUE des écritures, sans aucune lecture sous elles.
  'usersRoles',
  'systemSettings',
  // La supervision des tontines (lot 5 de PLAN_TONTINE) : deux lectures sous
  // `TONTINE_VIEW`, et la dissolution d'une tontine démarrée par la route
  // unique des actions, à deux signatures. Livrées avec leur écran, et non
  // avant — la famille D-25 n'a pas reçu de onzième membre.
  'communityProducts',
]);

/** L'écran métier de ce module est-il livré ? */
export function hasDeliveredScreen(module: BackOfficeModule): boolean {
  return deliveredModuleIds.has(module.id);
}

/** Le module d'accueil : la racine y redirige. */
export const homeModulePath = '/dashboard';

/**
 * La route de l'écran de connexion (`B-01`).
 *
 * **Elle n'est pas un module**, et c'est délibéré : les seize modules vivent
 * dans la coquille applicative — barre latérale, barre supérieure, contenu — et
 * la connexion est ce qui précède la coquille. L'ajouter à `backOfficeModules`
 * lui donnerait une entrée de navigation, un titre de barre supérieure et une
 * place dans un groupe, trois choses qu'elle ne doit pas avoir ; et les trois
 * contrôles qui exigent d'y trouver **exactement seize** modules diraient
 * dix-sept.
 */
export const loginPath = '/login';

/** Les modules d'un groupe, dans l'ordre de la table. */
export function modulesOfGroup(group: ModuleGroup): readonly BackOfficeModule[] {
  return backOfficeModules.filter((module) => module.group === group);
}

/**
 * Le module servi par [path], ou `undefined` — **préfixe compris**.
 *
 * Un module peut ouvrir des écrans SOUS son chemin : la fiche d'une opération
 * vit en `/transactions/{id}`, celle d'un acteur en
 * `/transactions/accounts/{id}`. Une correspondance exacte y aurait rendu
 * `undefined`, et la barre supérieure aurait alors affiché le titre de l'écran
 * d'erreur au-dessus d'une fiche parfaitement chargée — tandis que la barre
 * latérale aurait perdu la marque de son module courant.
 *
 * La comparaison porte sur `chemin/` et non sur `chemin` seul : sans la barre,
 * `/transactions` capterait `/transactions-archivees`, qui n'est pas le sien.
 */
export function moduleOfPath(path: string): BackOfficeModule | undefined {
  return backOfficeModules.find(
    (module) => module.path === path || path.startsWith(`${module.path}/`),
  );
}

/**
 * La clé de traduction du **libellé de navigation** d'un module.
 *
 * Ce n'est pas son titre : la maquette écrit « Identités & KYC » dans la barre
 * latérale et « Revue d'un dossier d'identité » en tête de l'écran, et les
 * deux sont voulus — le menu doit tenir dans 212 points, le titre doit dire ce
 * que l'écran fait.
 */
export function moduleNavLabelKey(module: BackOfficeModule): string {
  return `modules.${module.id}.navLabel`;
}

/** La clé de traduction du titre d'un module. */
export function moduleTitleKey(module: BackOfficeModule): string {
  return `modules.${module.id}.title`;
}

/** La clé de traduction du sous-titre d'un module. */
export function moduleSubtitleKey(module: BackOfficeModule): string {
  return `modules.${module.id}.subtitle`;
}

/**
 * Les clés de l'**invite de recherche**, propre au module puis générique.
 *
 * La maquette donne à chaque module la sienne — « Nom, numéro, référence
 * KYC… » sur Clients, « Référence, transaction orpheline… » sur Finance,
 * « Utilisateur, action, cible… » sur le journal d'audit. Ce n'est pas de la
 * rédaction : l'invite dit à l'agent **ce qu'il peut chercher ici**, ce
 * qu'une invite unique ne dit à personne.
 *
 * Deux clés, et dans cet ordre : celle du module, puis celle de la barre. La
 * maquette ne configure pas les quinze modules — la revue d'un dossier
 * d'identité n'y porte aucun champ de recherche —, et inventer une invite pour
 * ceux qu'elle laisse vides reviendrait à écrire dans l'écran ce que le dessin
 * n'a pas dit. i18next prend la première clé qui existe ;
 * `test/app/topbar-maquette.test.tsx` confronte les deux ensembles à la
 * maquette.
 */
export function moduleSearchPlaceholderKeys(module: BackOfficeModule): [string, string] {
  return [`modules.${module.id}.searchPlaceholder`, 'topbar.searchPlaceholder'];
}

/**
 * Ce module est-il utilisable par l'opérateur, d'après ce que la matrice a répondu ?
 *
 * **Trois cas, et le repli est « oui ».**
 *
 *  - [modules] vaut `null` — la réponse n'est pas encore là, ou l'appel a échoué : tout
 *    est rendu. Vider la barre latérale parce qu'une requête a mal tourné laisserait un
 *    opérateur habilité devant un menu vide, sans rien pour le lui expliquer.
 *  - Le module n'a pas de [BackOfficeModule.permissionModule], ou l'annexe ne le déclare
 *    pas décidable : rendu. La matrice ne sait rien en dire, et « pas encore décrit »
 *    n'est pas « interdit ».
 *  - Sinon : rendu si et seulement si l'opérateur peut y consulter quelque chose.
 *
 * **Rendre ou non ne décide de rien.** Le serveur refuse par ses propres contrôles, et
 * un opérateur qui taperait l'adresse à la main obtiendrait exactement le même refus
 * (`EX-BO-002`). Les routes, elles, restent toutes déclarées : masquer une entrée de
 * menu n'est pas retirer une destination.
 */
export function isModuleUsable(
  module: BackOfficeModule,
  modules: {
    readonly decidableModules: readonly string[];
    readonly readableModules: readonly string[];
  } | null,
): boolean {
  if (modules === null) return true;
  const gouvernant = module.permissionModule;
  if (gouvernant === undefined) return true;
  if (!modules.decidableModules.includes(gouvernant)) return true;
  return modules.readableModules.includes(gouvernant);
}
